import React, { useLayoutEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { ApolloClient, ApolloLink, ApolloProvider, Observable, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { accountSessionKey, acquireAuthenticationToken, isInteractionRequiredError } from "../authUtils";
import { notifyInteractionRequired } from "../authEvents";

// One client/cache per account scope. Clearing a shared cache is insufficient:
// an old in-flight response can repopulate it after an account switch.
export function createApolloSession({ instance, account, config, scopes, createCache, fetch }) {
  const key = accountSessionKey(account);
  let active = true;
  const isCurrent = () => active && accountSessionKey(instance.getActiveAccount()) === key;
  const requireCurrent = () => {
    if (!isCurrent()) {
      throw new Error("The application account changed");
    }
  };
  const boundary = new ApolloLink((operation, forward) => new Observable((observer) => {
    if (!isCurrent()) {
      observer.error(new Error("The application account changed"));
      return undefined;
    }
    const subscription = forward(operation).subscribe({
      next: (value) => isCurrent()
        ? observer.next(value)
        : observer.error(new Error("The application account changed")),
      error: (error) => observer.error(isCurrent() ? error : new Error("The application account changed")),
      complete: () => isCurrent()
        ? observer.complete()
        : observer.error(new Error("The application account changed")),
    });
    return () => subscription.unsubscribe();
  }));
  const auth = setContext(async (_, { headers }) => {
    requireCurrent();
    // Neither caller headers nor a previous context may choose credentials.
    const cleanHeaders = Object.fromEntries(Object.entries(headers || {}).filter(
      ([name]) => !["authorization", "x-api-key"].includes(name.toLowerCase()),
    ));
    if (!account) {
      return { headers: { ...cleanHeaders, "x-api-key": config.API_KEY } };
    }
    try {
      const token = await acquireAuthenticationToken(instance, account, scopes);
      requireCurrent();
      return { headers: { ...cleanHeaders, authorization: `Bearer ${token}` } };
    } catch (error) {
      if (isCurrent() && isInteractionRequiredError(error)) {
        notifyInteractionRequired(error);
      }
      // Employee auth failures never retry using the guest portal key.
      throw error;
    }
  });
  const client = new ApolloClient({
    link: boundary.concat(auth).concat(createHttpLink({ uri: config.GRAPHQL_URL, fetch })),
    cache: createCache(),
  });
  return {
    key,
    client,
    dispose() {
      active = false;
      client.stop();
    },
  };
}

export default function ApolloSessionProvider({ config, scopes, createCache, children }) {
  const { instance, accounts } = useMsal();
  const account = accounts.length ? instance.getActiveAccount() : null;
  const key = accountSessionKey(account);
  const session = useMemo(
    () => createApolloSession({ instance, account, config, scopes, createCache }),
    // Identity, not the refreshed AccountInfo object, defines the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instance, key, config, scopes, createCache],
  );
  useLayoutEffect(() => () => session.dispose(), [session]);
  return <ApolloProvider key={key} client={session.client}>{children}</ApolloProvider>;
}